import React, { useEffect, useCallback, memo } from 'react';
import axios from 'axios';
import { get } from 'lodash';
import config from './config/config';
import { setCompleteCompaniesData, setCompleteGlobalCompaniesData, setNotificationData } from './reducers/Watchlist';
import { setShowCompaniesDataLoader, setHideCompaniesDataLoader } from './reducers/Alerts';
import { getWatchlistCategories } from './components/watchlist/watchlistApiCalls';
import { setIsNewEmailNotification } from './reducers/User';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import SocketService from './socketService';
import { orderBy } from 'lodash';
import { overrideCompaniesDataInIndexDb, getCompaniesDataFromIndexDb } from './indexDb/dexie';
import { addDelayInTooltip } from 'global/GlobalActions';
import Highstock from 'highcharts/highstock';
import Highcharts from 'highcharts';
import { formatData } from 'components/watchlist/WatchlistHelpers';
import { getItemFromLocalStorage } from 'utils/helpers';

const REFREST_TIME = 3600000; // one hour
export const domesticKey = 'lastDomesticDataUpdatedTime';
export const globalKey = 'lastGlobalDataUpdatedTime';

const Cache = () => {
  const dispatch = useDispatch();
  const authenticationToken = useSelector(state => state.User.user?.authentication_token);
  const isNewEmailNotification = useSelector(state => state.User.isNewEmailNotification);

  const refreshIndexDB = useCallback(
    async apiParam => {
      try {
        const response = await axios.get('/api/get_companies_data', {
          params: {
            auth_token: authenticationToken,
            selected_type: apiParam,
            subject: 'all',
          },
        });
        let data = formatData(get(response, 'data.data.content', []));
        if (data?.length) {
          if (apiParam === 'domestic') {
            dispatch(setCompleteCompaniesData(data));
            await overrideCompaniesDataInIndexDb(config.indexDbDomesticTableName, data, domesticKey);
          } else if (apiParam === 'global') {
            dispatch(setCompleteGlobalCompaniesData(data));
            await overrideCompaniesDataInIndexDb(config.indexDbGlobalTableName, data, globalKey);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [dispatch, authenticationToken]
  );

  const cacheData = useCallback(
    async (tryToGetFromIndexDb = true) => {
      dispatch(setShowCompaniesDataLoader());
      try {
        if (tryToGetFromIndexDb) {
          const previousStoredData = await getCompaniesDataFromIndexDb(config.indexDbDomesticTableName);
          if (previousStoredData?.length) {
            dispatch(setCompleteCompaniesData(previousStoredData));
          } else {
            await refreshIndexDB('domestic');
            return;
          }
        }
        const savedDate = new Date(getItemFromLocalStorage(domesticKey, null, false));
        if (!savedDate || isNaN(savedDate.getTime()) || new Date() - savedDate > REFREST_TIME) {
          await refreshIndexDB('domestic');
        }
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setHideCompaniesDataLoader());
      }
    },
    [dispatch, refreshIndexDB]
  );

  const cacheDataGlobal = useCallback(
    async (tryToGetFromIndexDb = true) => {
      dispatch(setShowCompaniesDataLoader());
      try {
        if (tryToGetFromIndexDb) {
          const previousStoredData = await getCompaniesDataFromIndexDb(config.indexDbGlobalTableName);
          if (previousStoredData?.length) {
            dispatch(setCompleteGlobalCompaniesData(previousStoredData));
          } else {
            await refreshIndexDB('global');
            return;
          }
        }
        const savedDate = new Date(getItemFromLocalStorage(globalKey, null, false));
        if (!savedDate || isNaN(savedDate.getTime()) || new Date() - savedDate > REFREST_TIME) {
          await refreshIndexDB('global');
        }
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setHideCompaniesDataLoader());
      }
    },
    [dispatch, refreshIndexDB]
  );

  useEffect(() => {
    if (isNewEmailNotification) {
      axios
        .get('/api/email/notification')
        .then(response => {
          let data = get(response, 'data.data', []);
          if (data.length) {
            dispatch(setNotificationData(orderBy(data, ['email_time'], ['desc'])));
          } else {
            dispatch(setNotificationData([]));
          }
        })
        .catch(function(error) {
          dispatch(setNotificationData([]));
          // handle error
          console.log(error);
        });

      dispatch(setIsNewEmailNotification(false));
    }
  }, [dispatch, isNewEmailNotification]);

  useEffect(() => {
    cacheData(true);
    cacheDataGlobal(true);

    const refreshDataAfterInterval = setInterval(() => {
      cacheData(false);
      cacheDataGlobal(false);
    }, [600000]); // 10 minutes
    return () => clearInterval(refreshDataAfterInterval);
  }, [cacheData, cacheDataGlobal]);

  useEffect(() => {
    const socket = io.connect(config.socketUrl);
    SocketService.init(socket);
  }, []);

  useEffect(() => {
    dispatch(getWatchlistCategories());
  }, [dispatch]);

  useEffect(() => {
    addDelayInTooltip(Highstock);
    addDelayInTooltip(Highcharts);
  }, []);

  return <></>;
};

export default memo(Cache);
